import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import type { Courier, Order, Route } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { QrCode } from "../../ui/QrCode";

interface Props {
  route: Route;
  /** The deliveries were given to a courier ("Meegeven aan"). */
  onGiven: (orders: Order[]) => void;
  onClose: () => void;
}

/**
 * "➜ Navigeer" (prototype openRoute, "Route starten"): the stops in the order of the shortest drive from the
 * shop, Google Maps from the shop on this till, a QR code with the route for the courier's phone (from
 * wherever he is), and "Meegeven aan" each courier in service.
 */
export function RouteDialog({ route, onGiven, onClose }: Props) {
  const { api, toast } = useTill();
  const text = t.orders.route;
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = route.stops.length;

  useEffect(() => {
    const controller = new AbortController();
    api
      .request<{ couriers: Courier[] }>("GET", "/device/couriers", { retry: true, signal: controller.signal })
      .then((answer) => {
        setCouriers(answer.couriers);
      })
      .catch(() => undefined); // without the list there are simply no "Meegeven aan" buttons
    return () => {
      controller.abort();
    };
  }, [api]);

  const give = async (courier: Courier) => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.request<{ orders: Order[] }>("POST", "/device/deliveries/courier", {
        body: { orderIds: route.stops.map((stop) => stop.orderId), courierId: courier.id },
      });
      onGiven(answer.orders);
      toast(text.given(count, courier.name));
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  const paid = (stop: Route["stops"][number]) =>
    stop.dueCents > 0
      ? t.orders.unpaid
      : stop.paidWith.map((method) => t.paymentMethods[method]).join(" + ") || t.orders.statuses.paid;

  return (
    <Dialog title={text.title(count)} onClose={onClose}>
      <div className="routegrid">
        <ol className="stops">
          <li className="start">
            <b>{text.start}</b> {route.start}
          </li>
          {route.stops.map((stop, index) => (
            <li key={stop.orderId}>
              <b>
                {index + 1}. {stop.address}
              </b>
              <br />
              <span className="proto">
                <span className="code">{stop.code}</span> · {paid(stop)} · {formatEuro(stop.totalCents)}
              </span>
            </li>
          ))}
        </ol>
        <div className="rqr">
          <QrCode text={route.courierLink} label={text.qrLabel} />
          <span className="proto">{text.qr}</span>
        </div>
      </div>
      <div className="routeacts">
        <a className="btn primary" href={route.navigationUrl} target="_blank" rel="noopener noreferrer">
          {text.open}
        </a>
        {couriers.map((courier) => (
          <button key={courier.id} type="button" className="btn" disabled={busy} onClick={() => void give(courier)}>
            {text.give(courier.name)}
          </button>
        ))}
      </div>
      <p className="note">{count > 1 ? (route.fastest ? text.noteMany : text.noteRough) : text.noteOne}</p>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
